import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GameRoom } from './game-room';
import { PlayerResult, PlayerTeamRequest, RoomResult, UserResult } from '@shared/api-dto';
import { useQRCode } from '../../api/hooks';
import { kickPlayer, setPromptFormats, updateTeams } from '../../api/endpoints';
import { useTallies } from './shared/room-data-helpers';
import { RoundTypePicker, useSelectedRoundTypes } from './shared/round-type-picker';
import { Sparkles } from '../sparkles';

const MIN_PLAYERS = 4;

type HostRoomProps = {
  roomData: RoomResult;
};

export const HostRoom = ({ roomData }: HostRoomProps) => {
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [qrError, qrDataUrl] = useQRCode(roomData.code);
  const { prePrompts } = useTallies(roomData);
  const { selected, toggle, hasAny } = useSelectedRoundTypes();
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    const selectedTypes = new Set(selected);
    const selectedGuids = new Set(
      prePrompts.filter(p => selectedTypes.has(p.type)).map(p => p.guid)
    );
    const responses = roomData.responses;
    for (let i = 0; i < responses.length; i++) {
      const r = responses[i];
      if (!selectedGuids.has(r.promptGuid)) {
        continue;
      }
      if (!result[r.userGuid]) {
        result[r.userGuid] = 0;
      }
      ++result[r.userGuid];
    }
    return result;
  }, [roomData.responses, prePrompts, selected]);

  const selectedPrePrompts = useMemo(() => {
    const selectedTypes = new Set(selected);
    return prePrompts.filter(p => selectedTypes.has(p.type));
  }, [prePrompts, selected]);

  // Keep the room's prompts in sync so players only fill selected round types.
  useEffect(() => {
    if (!hasAny || roomData.isActive) {
      return;
    }
    const timer = window.setTimeout(() => {
      setPromptFormats({
        code: roomData.code,
        promptFormats: selectedRef.current,
      }).catch(e => {
        console.error('failed to update round types', e);
        setErrorMsg('failed to update round types');
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [selected, hasAny, roomData.code, roomData.isActive]);

  // Game is only ready to start when 4+ players have completed prompts.
  const allPlayersReady = roomData.players.filter(p => !p.inactive).length >= MIN_PLAYERS;
  const isReady = useMemo(() => {
    if (!hasAny) {
      return false;
    }
    // If there are no pre-prompts, just check for minimum player count
    if (selectedPrePrompts.length === 0) {
      return allPlayersReady;
    }
    // Otherwise check that enough players have completed their prompts
    return Object.values(counts).reduce((acc, completed) => (
      (completed >= selectedPrePrompts.length) ? acc + 1 : acc
    ), 0) >= MIN_PLAYERS;
  }, [counts, selectedPrePrompts.length, allPlayersReady, hasAny]);

  const onStart = async () => {
    try {
      await updateTeams(roomData.code, getRandomTeams(roomData.players), selected);
    } catch (e) {
      console.error('failed to update teams', e);
      setErrorMsg('failed to update teams');
    }
  };

  const onLeave = () => {
    navigate('/');
  };

  // Consolidate error messages.
  let finalError = '';
  if (qrError) {
    console.error(qrError);
    finalError = qrError;
  } else if (errorMsg) {
    finalError = errorMsg;
  }

  if (roomData.isActive) {
    return <GameRoom roomData={roomData} />;
  }

  const total = selectedPrePrompts.length;
  const activePlayers = roomData.players.filter(p => !p.inactive);
  const isPlayerReady = (guid: string) => total === 0 || (counts[guid] ?? 0) >= total;
  const readyCount = activePlayers.filter(p => isPlayerReady(p.user.guid)).length;
  const waitingOn = activePlayers.filter(p => !isPlayerReady(p.user.guid)).map(p => p.user.name);

  // Everyone who joined has finished their prompts and there are enough of
  // them: the lobby switches into its celebratory "let's go" state.
  const everyoneReady = isReady && waitingOn.length === 0 && activePlayers.length > 0;

  let statusText: string;
  if (!hasAny) {
    statusText = 'Select at least one round type to play.';
  } else if (roomData.players.length === 0) {
    statusText = 'Waiting for players to join...';
  } else if (waitingOn.length > 0) {
    statusText = `Waiting for ${listNames(waitingOn)}...`;
  } else if (!isReady) {
    statusText = `Need at least ${MIN_PLAYERS} ready players to start.`;
  } else {
    statusText = 'Everyone is ready!';
  }

  return (
    <div className={`lobby${everyoneReady ? ' allReady' : ''}`}>
      {everyoneReady && <Sparkles className='lobbyDecor' />}
      <div className='lobbyTitle'>
        <h1 className='chevrons'>
          {everyoneReady ? 'Everybody\u2019s in!' : 'Everybody in?'}
        </h1>
        <p>
          {everyoneReady
            ? 'Teams get picked as soon as you hit start.'
            : 'Grab your phone and join the room.'}
        </p>
      </div>
      {finalError && (<p className='red center'>{'' + finalError}</p>)}
      <div className='lobbyBody'>
        <div className='joinPanel'>
          <h2>Scan to join</h2>
          {qrDataUrl
            ? (
              <img className='qr' src={qrDataUrl} alt={`QR code for room ${roomData.code}`} />
              )
            : (
              <span className='qr'></span>
              )}
          <p className='joinLabel'>Room code</p>
          <div className='roomCodeBig'>{roomData.code}</div>
          <p className='joinHint'>Point your phone camera at the code</p>
        </div>
        <div className='playersPanel'>
          <div className='playersHeading'>
            <h2>Players</h2>
            <span className='pill'>{`${readyCount} of ${activePlayers.length} ready`}</span>
          </div>
          <div className='playerList'>
            {roomData.players.map((p, i) => (
              <PlayerProgress
                key={p.user.guid}
                roomData={roomData}
                playerNum={i + 1}
                user={p.user}
                inactive={!!p.inactive}
                counts={counts}
                total={total}
                onError={setErrorMsg}
              />
            ))}
          </div>
          {everyoneReady
            ? (
              <p className='waitingText'>
                <span className='readyBanner'>{statusText}</span>
              </p>
              )
            : (
              <p className='waitingText'>{statusText}</p>
              )}
          <div className='roundTypeStart'>
            <RoundTypePicker selected={selected} onToggle={toggle} />
          </div>
          <div className='lobbyActions'>
            <button className='back' onClick={onLeave}>Leave room</button>
            <button
              className={everyoneReady ? 'shine startGame' : 'startGame'}
              disabled={!isReady}
              onClick={onStart}
            >
              Start game
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function listNames (names: string[]): string {
  if (names.length <= 2) {
    return names.join(' and ');
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function getRandomTeams (players: PlayerResult[]): PlayerTeamRequest[] {
  const active: PlayerResult[] = [];
  const inactive: PlayerResult[] = [];
  players.forEach(p => {
    if (p.inactive) {
      inactive.push(p);
    } else {
      active.push(p);
    }
  });

  let curTeam = true;
  const assign = (ps: PlayerResult[]): PlayerTeamRequest[] => {
    const rs: PlayerTeamRequest[] = [];
    while (ps.length > 0) {
      const i = Math.floor(Math.random() * ps.length);
      const [p] = ps.splice(i, 1);
      rs.push({
        playerGuid: p.user.guid,
        team: curTeam ? 1 : 2
      });
      curTeam = !curTeam;
    }
    return rs;
  };

  return assign(active).concat(assign(inactive));
}

type PlayerProgressType = {
  roomData: RoomResult;
  playerNum: number;
  user: UserResult;
  inactive?: boolean;
  counts: Record<string, number>;
  total: number;
  onError: (msg: string) => void;
}

export const PlayerProgress = ({
  roomData,
  playerNum,
  user,
  inactive = false,
  counts,
  total,
  onError
}: PlayerProgressType) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const completed = counts[user.guid] ?? 0;
  const percentage = total > 0 ? Math.max(4, (completed / total) * 100) : 100;
  const ready = completed >= total || total === 0;
  // Alternate avatar/progress colors down the list.
  const tone = playerNum % 2 === 0 ? 'pink' : 'blue';

  const onKick = async () => {
    try {
      setIsLoading(true);
      await kickPlayer({
        code: roomData.code,
        playerGuid: user.guid
      });
    } catch (e: any) {
      setIsLoading(false);
      console.error('could not kick player', e);
      onError(`Could not remove ${user.name} from room`);
    }
  };

  const className = [
    'playerRow',
    tone,
    ready ? 'ready' : '',
    isLoading ? 'loading' : '',
    inactive ? 'inactive' : ''
  ].filter(Boolean).join(' ');

  // Lets the "everybody's in" celebration stagger down the list.
  const style = { '--i': playerNum - 1 } as React.CSSProperties;

  return (
    <div className={className} style={style}>
      <span className={`avatar ${tone}`}>{user.name.trim().charAt(0)}</span>
      <div className='playerRowMain'>
        <div className='playerRowTop'>
          <span className='playerName'>{user.name}</span>
          {ready
            ? (
              <span className='playerStatus ready'>
                <span className='readyCheck' aria-hidden='true'></span>
                {inactive ? 'Left' : 'Ready'}
              </span>
              )
            : (
              <span className='playerStatus'>{`Answering \u00b7 ${completed} of ${total}`}</span>
              )}
        </div>
        {!ready && (
          <div className='progressContainer'>
            <div className='progressIndicator' style={{ width: `${percentage}%` }}></div>
          </div>
        )}
      </div>
      <span
        onClick={onKick}
        className='kickPlayer'
        role='button'
        title={`Remove ${user.name} from the room`}
        aria-label={`Remove ${user.name} from the room`}
      >
        {'\u00d7'}
      </span>
    </div>
  );
};
